// ServicesDetails.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  FiSearch, FiEye, FiEdit, FiTrash2, FiDownload, FiRefreshCw,
  FiArrowLeft, FiClock, FiMapPin, FiUser, FiDollarSign, FiCalendar,
  FiNavigation, FiMap, FiLoader, FiRotateCcw
} from 'react-icons/fi';

import {
  fetchMasterData,
  searchBuses,
  deactivateBus,
  activateBus,
  fetchTravelHistory,
  updateSearchParam,
  setOperatorBySelection,
  setStateBySelection,
  setZoneBySelection,
  clearAllFilters,
  setSelectedService,
  setShowDetailsModal,
  setShowTravelHistoryModal,
  clearTravelHistory,
} from '../../redux/Slices/servicesSlice';

// ---------- Service Details Modal Component ----------
const ServiceDetailsModal = ({ service, onClose, onViewTravelHistory }) => {
  const { t } = useTranslation();
  if (!service) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('serviceDetailsTitle', 'Service Details')}: {service.busRegistrationNumber}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        
        <div className="service-details-grid">
          <div className="detail-card">
            <div className="detail-card__header">
              <FiMapPin className="detail-icon" />
              <h3>{t('busInformation', 'Bus Information')}</h3>
            </div>
            <div className="detail-card__content">
              <div className="detail-row">
                <span className="detail-label">{t('busNumber', 'Bus Number')}:</span>
                <span className="detail-value">{service.busNo}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('busName', 'Bus Name')}:</span>
                <span className="detail-value">{service.busName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('registrationNumber', 'Registration Number')}:</span>
                <span className="detail-value">{service.busRegistrationNumber}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('contactNumber', 'Contact Number')}:</span>
                <span className="detail-value">{service.busContactNumber}</span>
              </div>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-card__header">
              <FiUser className="detail-icon" />
              <h3>{t('operatorInformation', 'Operator Information')}</h3>
            </div>
            <div className="detail-card__content">
              <div className="detail-row">
                <span className="detail-label">{t('operatorNumber', 'Operator Number')}:</span>
                <span className="detail-value">{service.operatorNumber}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('operatorName', 'Operator Name')}:</span>
                <span className="detail-value">{service.operator?.operatorName || t('na', 'N/A')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('contact', 'Contact')}:</span>
                <span className="detail-value">{service.operator?.busOperatorContactNumber || t('na', 'N/A')}</span>
              </div>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-card__header">
              <FiNavigation className="detail-icon" />
              <h3>{t('routeInformation', 'Route Information')}</h3>
            </div>
            <div className="detail-card__content">
              <div className="detail-row">
                <span className="detail-label">{t('routePathNo', 'Route Path No')}:</span>
                <span className="detail-value">{service.routePathNo}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('routePathName', 'Route Path Name')}:</span>
                <span className="detail-value">{service.routePath?.routePathName || t('na', 'N/A')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('routeNo', 'Route No')}:</span>
                <span className="detail-value">{service.routePath?.routeNo || t('na', 'N/A')}</span>
              </div>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-card__header">
              <FiClock className="detail-icon" />
              <h3>{t('administrativeDetails', 'Administrative Details')}</h3>
            </div>
            <div className="detail-card__content">
              <div className="detail-row">
                <span className="detail-label">{t('stateCode', 'State Code')}:</span>
                <span className="detail-value">{service.stateCode}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('stateName', 'State Name')}:</span>
                <span className="detail-value">{service.state?.stateName || t('na', 'N/A')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('zoneCode', 'Zone Code')}:</span>
                <span className="detail-value">{service.zoneCode}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('zoneName', 'Zone Name')}:</span>
                <span className="detail-value">{service.zone?.zoneName || t('na', 'N/A')}</span>
              </div>
            </div>
          </div>

          <div className="detail-card">
            <div className="detail-card__header">
              <FiClock className="detail-icon" />
              <h3>{t('serviceStatus', 'Service Status')}</h3>
            </div>
            <div className="detail-card__content">
              <div className="detail-row">
                <span className="detail-label">{t('status', 'Status')}:</span>
                <span className={`detail-value status-badge ${service.status ? 'status-active' : 'status-inactive'}`}>
                  {service.status ? t('active', 'Active') : t('inactive', 'Inactive')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            {t('close', 'Close')}
          </button>
          <button 
            className="btn-primary" 
            onClick={() => {
              onClose();
              onViewTravelHistory(service);
            }}
          >
            <FiEye /> {t('viewTravelHistory', 'View Travel History')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Travel History Modal Component ----------
const TravelHistoryModal = ({ service, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { travelHistory, travelHistoryLoading, arrivalAndDeparture } = useSelector(state => state.services);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    if (service) {
      dispatch(fetchTravelHistory(service.busNo));
    }
    return () => {
      dispatch(clearTravelHistory());
    };
  }, [service, dispatch]);

  const filteredHistory = filter === 'all' 
    ? travelHistory 
    : travelHistory.filter(trip => trip.status === filter);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('travelHistoryTitle', 'Travel History')}: {service?.busRegistrationNumber || t('busService', 'Bus Service')}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label>{t('filterByStatus', 'Filter by Status')}:</label>
            <select 
              className="filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">{t('allTrips', 'All Trips')}</option>
              <option value="Completed">{t('completed', 'Completed')}</option>
              <option value="Ongoing">{t('ongoing', 'Ongoing')}</option>
              <option value="Cancelled">{t('cancelled', 'Cancelled')}</option>
              <option value="Delayed">{t('delayed', 'Delayed')}</option>
            </select>
          </div>
          
          <div className="stats-summary">
            <div className="stat-card">
              <FiCalendar className="stat-icon" />
              <div>
                <span className="stat-value">{travelHistory.length}</span>
                <span className="stat-label">{t('totalTrips', 'Total Trips')}</span>
              </div>
            </div>
            <div className="stat-card">
              <FiUser className="stat-icon" />
              <div>
                <span className="stat-value">{travelHistory.reduce((sum, trip) => sum + (trip.passengers || 0), 0)}</span>
                <span className="stat-label">{t('totalPassengers', 'Total Passengers')}</span>
              </div>
            </div>
            <div className="stat-card">
              <FiDollarSign className="stat-icon" />
              <div>
                <span className="stat-value">${travelHistory.reduce((sum, trip) => sum + (trip.revenue || 0), 0)}</span>
                <span className="stat-label">{t('totalRevenue', 'Total Revenue')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="table-container">
          {travelHistoryLoading ? (
            <div className="loading-spinner">
              <FiLoader className="spinning" /> {t('loadingTravelHistory', 'Loading travel history...')}
            </div>
          ) : (
            <>
              {travelHistory.length === 0 ? (
                <div className="no-data-message">
                  {t('noTravelHistory', 'No travel history available for this bus.')}
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('date', 'Date')}</th>
                      <th>{t('startTime', 'Start Time')}</th>
                      <th>{t('endTime', 'End Time')}</th>
                      <th>{t('driver', 'Driver')}</th>
                      <th>{t('busNumber', 'Bus Number')}</th>
                      <th>{t('passengers', 'Passengers')}</th>
                      <th>{t('revenue', 'Revenue')}</th>
                      <th>{t('incidents', 'Incidents')}</th>
                      <th>{t('status', 'Status')}</th>
                      <th>{t('actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.map((trip) => (
                      <tr key={trip.id}>
                        <td>{trip.date}</td>
                        <td>{trip.startTime}</td>
                        <td>{trip.endTime}</td>
                        <td>{trip.driver}</td>
                        <td>{trip.busNumber}</td>
                        <td>{trip.passengers}</td>
                        <td>${trip.revenue}</td>
                        <td>
                          <span className={`incident-tag ${trip.incidents === 'None' ? 'no-incident' : 'has-incident'}`}>
                            {trip.incidents || t('none', 'None')}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${trip.status?.toLowerCase() || 'completed'}`}>
                            {trip.status || t('completed', 'Completed')}
                          </span>
                        </td>
                        <td>
                          <button className="btn-icon" title={t('viewDetails', 'View Details')}>
                            <FiEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            {t('close', 'Close')}
          </button>
          <button className="btn-outline" disabled={travelHistory.length === 0}>
            <FiDownload /> {t('exportHistory', 'Export History')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Main Services Details Page Component ----------
const ServicesDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // All state from Redux slice
  const {
    operators,
    states,
    zones,
    masterDataLoading,
    searchParams,
    services,
    loading,
    searchPerformed,
    selectedService,
    showDetailsModal,
    showTravelHistoryModal,
    activatingId,
    deactivatingId,
    arrivalAndDeparture
  } = useSelector(state => state.services);
  
  console.log(arrivalAndDeparture);

  // Track if we've already refreshed to prevent infinite loops
  const [hasRefreshed, setHasRefreshed] = useState(false);

  // Sort operators alphabetically by name
  const sortedOperators = useMemo(() => {
    return [...operators].sort((a, b) => a.operatorName.localeCompare(b.operatorName));
  }, [operators]);

  // Sort states alphabetically by name
  const sortedStates = useMemo(() => {
    return [...states].sort((a, b) => a.stateName.localeCompare(b.stateName));
  }, [states]);

  // Sort zones alphabetically by name
  const sortedZones = useMemo(() => {
    return [...zones].sort((a, b) => a.zoneName.localeCompare(b.zoneName));
  }, [zones]);

  // Fetch master data on component mount
  useEffect(() => {
    dispatch(fetchMasterData());
  }, [dispatch]);

  // Function to refresh data based on current search state
  const refreshData = () => {
    if (searchPerformed && Object.values(searchParams).some(v => v && v !== '')) {
      // If search was performed, refresh search results
      dispatch(searchBuses(searchParams));
    } else if (searchPerformed && services.length > 0) {
      // If we have search results but no active filters, refresh the last search
      dispatch(searchBuses(searchParams));
    } else {
      // Otherwise just refresh master data
      dispatch(fetchMasterData());
    }
  };

  // Listen for navigation state to refresh data when coming back from edit page
  useEffect(() => {
    if (location.state?.refresh && !hasRefreshed) {
      // Clear the state to prevent multiple refreshes
      window.history.replaceState({}, document.title);
      
      // Small delay to ensure the page is fully loaded
      const timer = setTimeout(() => {
        refreshData();
        setHasRefreshed(true);
      }, 100);
      
      return () => clearTimeout(timer);
    }
    
    // Reset refresh flag when component unmounts or when location changes without refresh flag
    if (!location.state?.refresh) {
      setHasRefreshed(false);
    }
  }, [location.state, hasRefreshed]);

  // Search handlers
  const handleSearch = () => {
    dispatch(searchBuses(searchParams));
  };

  // ---------- Clear All: resets state AND re-fetches full list ----------
  const handleClearAll = () => {
    // 1. Reset filter fields + searchPerformed flag in Redux
    dispatch(clearAllFilters());
    // 2. Re-fetch the complete list of bus services so the table shows everything
    dispatch(searchBuses({
      operatorName: '',
      operatorNumber: '',
      stateName: '',
      stateCode: '',
      zoneName: '',
      zoneCode: '',
    }));
  };

  // ---------- FIXED: allow reselecting the placeholder option ----------
  const handleOperatorSelect = (operatorName) => {
    const selectedOperator = operators.find(op => op.operatorName === operatorName);
    dispatch(setOperatorBySelection({
      operatorName,
      // When placeholder is selected, selectedOperator is undefined → clear the number too
      operatorNumber: selectedOperator?.operatorNumber || '',
    }));
  };

  const handleStateSelect = (stateName) => {
    const selectedState = states.find(state => state.stateName === stateName);
    dispatch(setStateBySelection({
      stateName,
      stateCode: selectedState?.stateCode || '',
    }));
  };

  const handleZoneSelect = (zoneName) => {
    const selectedZone = zones.find(zone => zone.zoneName === zoneName);
    dispatch(setZoneBySelection({
      zoneName,
      zoneCode: selectedZone?.zoneCode || '',
    }));
  };

  const handleViewDetails = (service) => {
    dispatch(setSelectedService(service));
    dispatch(setShowDetailsModal(true));
  };

  const handleViewTravelHistory = (service) => {
    dispatch(setSelectedService(service));
    dispatch(setShowTravelHistoryModal(true));
  };

  const handleEditService = (service) => {
    navigate(`/bus-finder/bus-services/edit/${service.busNo}`, { state: { busData: service } });
  };

  const handleDeactivate = (service) => {
    if (window.confirm(t('deactivateConfirm', 'Are you sure you want to deactivate this bus service? This will set the status to inactive.'))) {
      dispatch(deactivateBus(service));
    }
  };

  const handleActivate = (service) => {
    if (window.confirm(t('activateConfirm', 'Are you sure you want to reactivate this bus service? It will become active again.'))) {
      dispatch(activateBus(service.busNo));
    }
  };

  const handleStatusToggle = (service) => {
    if (activatingId === service.busNo || deactivatingId === service.busNo) return;
    if (service.status) {
      handleDeactivate(service);
    } else {
      handleActivate(service);
    }
  };

  if (masterDataLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiLoader className="spinning" /> {t('loadingMasterData', 'Loading master data...')}
        </div>
      </div>
    );
  }

  return (
    <div className="services-details-page">
      <div>
        <button className="btn-back" onClick={() => navigate(-1)}>
          <FiArrowLeft color='black'/> {t('back', 'Back')}
        </button>
      </div>

      <div className="search-filters-section">
        <div className="detailed-search-grid">
          <div className="search-field-group">
            <label>{t('operatorName', 'Operator Name')}</label>
            <select
              className="search-select"
              name="operatorName"
              value={searchParams.operatorName}
              onChange={(e) => handleOperatorSelect(e.target.value)}
            >
              <option value="">{t('selectOperator', 'Select Operator')}</option>
              {sortedOperators.map((operator) => (
                <option key={operator.operatorNumber} value={operator.operatorName}>
                  {operator.operatorName} ({operator.operatorNumber})
                </option>
              ))}
            </select>
          </div>

          <div className="search-field-group">
            <label>{t('operatorNumber', 'Operator Number')}</label>
            <input
              type="text"
              name="operatorNumber"
              placeholder={t('autoFilledOperator', 'Auto-filled when selecting operator')}
              value={searchParams.operatorNumber}
              readOnly
              className="search-input"
            />
          </div>

          <div className="search-field-group">
            <label>{t('stateName', 'State Name')}</label>
            <select
              className="search-select"
              name="stateName"
              value={searchParams.stateName}
              onChange={(e) => handleStateSelect(e.target.value)}
            >
              <option value="">{t('selectState', 'Select State')}</option>
              {sortedStates.map((state) => (
                <option key={state.stateCode} value={state.stateName}>
                  {state.stateName} ({state.stateCode})
                </option>
              ))}
            </select>
          </div>

          <div className="search-field-group">
            <label>{t('stateCode', 'State Code')}</label>
            <input
              type="text"
              name="stateCode"
              placeholder={t('autoFilledState', 'Auto-filled when selecting state')}
              value={searchParams.stateCode}
              readOnly
              className="search-input"
            />
          </div>

          <div className="search-field-group">
            <label>{t('zoneName', 'Zone Name')}</label>
            <select
              className="search-select"
              name="zoneName"
              value={searchParams.zoneName}
              onChange={(e) => handleZoneSelect(e.target.value)}
            >
              <option value="">{t('selectZone', 'Select Zone')}</option>
              {sortedZones.map((zone) => (
                <option key={zone.zoneCode} value={zone.zoneName}>
                  {zone.zoneName} ({zone.zoneCode})
                </option>
              ))}
            </select>
          </div>

          <div className="search-field-group">
            <label>{t('zoneCode', 'Zone Code')}</label>
            <input
              type="text"
              name="zoneCode"
              placeholder={t('autoFilledZone', 'Auto-filled when selecting zone')}
              value={searchParams.zoneCode}
              readOnly
              className="search-input"
            />
          </div>
        </div>

        <div className="search-actions-row">
          <div className="search-button-group">
            <button 
              className="btn-primary" 
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FiLoader className="spinning" /> {t('searching', 'Searching...')}
                </>
              ) : (
                <>
                  <FiSearch /> {t('searchBuses', 'Search Buses')}
                </>
              )}
            </button>
            
            <button 
              className="btn-outline" 
              onClick={handleClearAll}
              disabled={loading}
            >
              {t('clearAll', 'Clear All')}
            </button>
          </div>

          <div className="action-buttons">
            <button 
              className="btn-icon" 
              title={t('refresh', 'Refresh')}
              onClick={refreshData}
              disabled={loading}
            >
              <FiRefreshCw className={loading ? 'spinning' : ''} />
            </button>
            <button 
              className="btn-icon" 
              title={t('exportData', 'Export Data')}
              disabled={services.length === 0 || loading}
            >
              <FiDownload />
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <h3>
            {searchPerformed ? t('searchResults', 'Search Results') : t('busServices', 'Bus Services')} 
            {searchPerformed && ` (${services.length} ${t('found', 'found')})`}
          </h3>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <FiLoader className="spinning" /> {t('loadingSearchResults', 'Loading search results...')}
          </div>
        ) : (
          <>
            {searchPerformed && services.length === 0 ? (
              <div className="no-data-message">
                {t('noServicesFound', 'No bus services found matching your search criteria.')}
              </div>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('busNo', 'Bus No.')}</th>
                      <th>{t('registrationNo', 'Registration No.')}</th>
                      <th>{t('operator', 'Operator')}</th>
                      <th>{t('routePath', 'Route Path')}</th>
                      <th>{t('state', 'State')}</th>
                      <th>{t('zone', 'Zone')}</th>
                      <th>{t('status', 'Status')}</th>
                      <th>{t('actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service) => (
                      <tr key={service.busNo}>
                        <td>
                          <span className="bus-number">{service.busNo}</span>
                        </td>
                        <td>
                          <span className="registration-number">{service.busRegistrationNumber}</span>
                        </td>
                        <td>
                          <div className="operator-info">
                            <strong>{service.operatorNumber}</strong>
                            <div className="operator-name">{service.operator?.operatorName || t('na', 'N/A')}</div>
                          </div>
                        </td>
                        <td>
                          <div className="route-info">
                            <span className="route-path">{service.routePathNo}</span>
                            <div className="route-name">{service.routePath?.routePathName || t('na', 'N/A')}</div>
                          </div>
                        </td>
                        <td>
                          <div className="state-info">
                            <FiMap className="state-icon" />
                            <span>{service.stateCode}</span>
                            <div className="state-name">{service.state?.stateName || t('na', 'N/A')}</div>
                          </div>
                        </td>
                        <td>
                          <div className="zone-info">
                            <span>{service.zoneCode}</span>
                            <div className="zone-name">{service.zone?.zoneName || t('na', 'N/A')}</div>
                          </div>
                        </td>
                        <td>
                          <span 
                            className={`status-badge clickable-status ${service.status ? 'status-active' : 'status-inactive'}`}
                            onClick={() => handleStatusToggle(service)}
                            style={{ cursor: (activatingId === service.busNo || deactivatingId === service.busNo) ? 'wait' : 'pointer' }}
                            title={t('clickToToggleStatus', 'Click to toggle status')}
                          >
                            {(activatingId === service.busNo || deactivatingId === service.busNo) ? (
                              <FiLoader className="spinning" size={14} />
                            ) : (
                              service.status ? t('active', 'Active') : t('inactive', 'Inactive')
                            )}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons-group">
                            <button 
                              className="btn-icon" 
                              title={t('viewDetails', 'View Details')}
                              onClick={() => handleViewDetails(service)}
                            >
                              <FiEye />
                            </button>
                            <button 
                              className="btn-icon" 
                              title={t('editService', 'Edit Service')}
                              onClick={() => handleEditService(service)}
                            >
                              <FiEdit />
                            </button>
                            {service.status ? (
                              <button 
                                className="btn-icon btn-danger" 
                                title={t('deactivateService', 'Deactivate Service')}
                                onClick={() => handleDeactivate(service)}
                                disabled={deactivatingId === service.busNo}
                              >
                                {deactivatingId === service.busNo ? <FiLoader className="spinning" /> : <FiTrash2 />}
                              </button>
                            ) : (
                              <button 
                                className="btn-icon btn-success" 
                                title={t('reactivateService', 'Reactivate Service')}
                                onClick={() => handleActivate(service)}
                                disabled={activatingId === service.busNo}
                              >
                                {activatingId === service.busNo ? <FiLoader className="spinning" /> : <FiRotateCcw />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {showDetailsModal && (
        <ServiceDetailsModal
          service={selectedService}
          onClose={() => dispatch(setShowDetailsModal(false))}
          onViewTravelHistory={handleViewTravelHistory}
        />
      )}

      {showTravelHistoryModal && (
        <TravelHistoryModal
          service={selectedService}
          onClose={() => dispatch(setShowTravelHistoryModal(false))}
        />
      )}

      <style jsx>{`
        .services-details-page {
          padding: 2rem;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .btn-back:hover {
          background: #f5f5f5;
        }

        .search-filters-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .detailed-search-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .search-field-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .search-field-group label {
          font-size: 0.85rem;
          font-weight: 500;
          color: #555;
        }

        .search-input, .search-select {
          padding: 0.6rem 0.75rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.9rem;
          background: white;
        }

        .search-input:focus, .search-select:focus {
          outline: none;
          border-color: #4CAF50;
        }

        .search-actions-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .search-button-group {
          display: flex;
          gap: 0.75rem;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .btn-primary {
          background: #4CAF50;
          color: white;
          border: none;
          padding: 0.6rem 1.2rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: #43a047;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-outline {
          background: white;
          border: 1px solid #ddd;
          padding: 0.6rem 1.2rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-outline:hover:not(:disabled) {
          background: #f5f5f5;
        }

        .btn-icon {
          background: white;
          border: 1px solid #ddd;
          padding: 0.6rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-icon:hover:not(:disabled) {
          background: #f5f5f5;
        }

        .btn-icon:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-danger {
          color: #dc3545;
        }

        .btn-danger:hover:not(:disabled) {
          background: #fee2e2;
        }

        .btn-success {
          color: #28a745;
        }
        .btn-success:hover:not(:disabled) {
          background: #e6f4ea;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .table-header {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .table-header h3 {
          margin: 0;
          font-size: 1.1rem;
          color: #333;
        }

        .responsive-table-wrapper {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 800px;
        }

        .data-table th {
          background: #f8f9fa;
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
          color: #555;
          border-bottom: 2px solid #eee;
          font-size: 0.85rem;
        }

        .data-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #eee;
          color: #333;
          font-size: 0.9rem;
          vertical-align: middle;
        }

        .data-table tr:hover {
          background: #f9f9f9;
        }

        .operator-info, .route-info, .state-info, .zone-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .operator-name, .route-name, .state-name, .zone-name {
          font-size: 0.8rem;
          color: #666;
        }

        .bus-number, .registration-number {
          font-weight: 500;
        }

        .state-icon {
          display: inline-block;
          margin-right: 0.25rem;
          font-size: 0.8rem;
          color: #888;
        }

        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .clickable-status {
          transition: all 0.2s;
          user-select: none;
        }
        .clickable-status:hover {
          filter: brightness(0.95);
          transform: scale(1.02);
        }

        .status-active {
          background: #d4edda;
          color: #155724;
        }

        .status-inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .action-buttons-group {
          display: flex;
          gap: 0.5rem;
        }

        .loading-spinner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 3rem;
          color: #666;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .no-data-message {
          text-align: center;
          padding: 3rem;
          color: #666;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 800px;
          max-height: 85vh;
          overflow-y: auto;
        }

        .wide-modal {
          max-width: 1100px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.2rem;
        }

        .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #999;
        }

        .service-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
          padding: 1.5rem;
        }

        .detail-card {
          background: #f8f9fa;
          border-radius: 8px;
          overflow: hidden;
        }

        .detail-card__header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: #e9ecef;
          border-bottom: 1px solid #dee2e6;
        }

        .detail-card__header h3 {
          margin: 0;
          font-size: 0.95rem;
        }

        .detail-card__content {
          padding: 0.75rem 1rem;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 0.25rem 0;
          font-size: 0.85rem;
        }

        .detail-label {
          color: #666;
        }

        .detail-value {
          font-weight: 500;
          color: #333;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid #eee;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
        }

        .filters-section {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-select {
          padding: 0.4rem 0.75rem;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        .stats-summary {
          display: flex;
          gap: 1rem;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #f8f9fa;
          padding: 0.5rem 1rem;
          border-radius: 8px;
        }

        .stat-icon {
          color: #4CAF50;
        }

        .stat-value {
          font-weight: 600;
          font-size: 1.1rem;
        }

        .stat-label {
          font-size: 0.7rem;
          color: #666;
          margin-left: 0.25rem;
        }

        .incident-tag {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
        }

        .no-incident {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .has-incident {
          background: #ffebee;
          color: #c62828;
        }

        @media (max-width: 768px) {
          .services-details-page {
            padding: 1rem;
          }
          
          .detailed-search-grid {
            grid-template-columns: 1fr;
          }
          
          .search-actions-row {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-button-group {
            justify-content: stretch;
          }
          
          .search-button-group button {
            flex: 1;
          }
          
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .stats-summary {
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
};

export default ServicesDetails;